/**
 * IGNITON PRODUCT COMPARISON SECTION
 * Enhanced interactions with Seed.com-style layout
 */

class ProductComparison {
  constructor() {
    this.table = document.querySelector('[data-comparison-table]');
    this.rows = document.querySelectorAll('[data-feature-row]');
    this.infoTriggers = document.querySelectorAll('.feature-info-btn');
    
    if (!this.table) return;
    
    this.init();
  }

  init() {
    this.setupScrollAnimation();
    this.setupTooltips();
    this.setupRowHoverEffects();
    this.observeVisibility();
  }

  /**
   * Setup scroll-triggered animations using Intersection Observer
   */
  observeVisibility() {
    if (!window.IntersectionObserver) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          
          // Trigger individual row animations
          this.animateRows();
        }
      });
    }, observerOptions);

    observer.observe(this.table);
  }

  /**
   * Animate rows with stagger effect
   */
  animateRows() {
    // Check if GSAP is available
    if (typeof gsap !== 'undefined') {
      gsap.from(this.rows, {
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        clearProps: 'all'
      });

      // Animate check marks with pop effect
      const checkIcons = this.table.querySelectorAll('.check-icon--filled');
      gsap.from(checkIcons, {
        scale: 0,
        opacity: 0,
        duration: 0.5,
        stagger: {
          amount: 0.8,
          from: 'start'
        },
        ease: 'back.out(1.7)',
        delay: 0.3,
        clearProps: 'all'
      });
    }
  }

  /**
   * Setup smooth scroll animation for horizontal overflow
   */
  setupScrollAnimation() {
    let isScrolling = false;
    
    this.table.addEventListener('scroll', () => {
      if (!isScrolling) {
        isScrolling = true;
        this.table.classList.add('is-scrolling');
      }

      clearTimeout(this.table.scrollTimeout);
      
      this.table.scrollTimeout = setTimeout(() => {
        isScrolling = false;
        this.table.classList.remove('is-scrolling');
      }, 150);
    });

    // Add momentum scrolling hint for touch devices
    if ('ontouchstart' in window) {
      this.table.style.webkitOverflowScrolling = 'touch';
    }
  }

  /**
   * Enhanced tooltip interactions
   */
  setupTooltips() {
    this.infoTriggers.forEach(trigger => {
      const tooltip = trigger.nextElementSibling;
      
      if (!tooltip || !tooltip.classList.contains('feature-tooltip')) return;

      // Keyboard accessibility
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleTooltip(trigger, tooltip);
        }
      });

      // Mouse events
      trigger.addEventListener('mouseenter', () => {
        this.showTooltip(tooltip);
      });

      trigger.addEventListener('mouseleave', () => {
        this.hideTooltip(tooltip);
      });

      // Focus events for accessibility
      trigger.addEventListener('focus', () => {
        this.showTooltip(tooltip);
      });

      trigger.addEventListener('blur', () => {
        this.hideTooltip(tooltip);
      });

      // Ensure tooltip stays within viewport
      this.adjustTooltipPosition(tooltip);
    });
  }

  toggleTooltip(trigger, tooltip) {
    const isVisible = tooltip.style.opacity === '1';
    
    if (isVisible) {
      this.hideTooltip(tooltip);
    } else {
      this.showTooltip(tooltip);
    }
  }

  showTooltip(tooltip) {
    if (typeof gsap !== 'undefined') {
      gsap.to(tooltip, {
        opacity: 1,
        visibility: 'visible',
        y: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    } else {
      tooltip.style.opacity = '1';
      tooltip.style.visibility = 'visible';
      tooltip.style.transform = 'translateY(0)';
    }
  }

  hideTooltip(tooltip) {
    if (typeof gsap !== 'undefined') {
      gsap.to(tooltip, {
        opacity: 0,
        visibility: 'hidden',
        y: -10,
        duration: 0.3,
        ease: 'power2.in'
      });
    } else {
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
      tooltip.style.transform = 'translateY(-10px)';
    }
  }

  adjustTooltipPosition(tooltip) {
    // Ensure tooltip doesn't overflow viewport
    const checkPosition = () => {
      const rect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (rect.right > viewportWidth - 20) {
        tooltip.style.left = 'auto';
        tooltip.style.right = '0';
      }

      if (rect.left < 20) {
        tooltip.style.left = '0';
        tooltip.style.right = 'auto';
      }
    };

    // Check on show
    tooltip.addEventListener('transitionstart', checkPosition);
  }

  /**
   * Row hover effects with subtle animations
   */
  setupRowHoverEffects() {
    this.rows.forEach(row => {
      row.addEventListener('mouseenter', () => {
        if (typeof gsap !== 'undefined') {
          const checks = row.querySelectorAll('.check-icon');
          gsap.to(checks, {
            scale: 1.15,
            duration: 0.3,
            ease: 'back.out(2)',
            stagger: 0.05
          });
        }
      });

      row.addEventListener('mouseleave', () => {
        if (typeof gsap !== 'undefined') {
          const checks = row.querySelectorAll('.check-icon');
          gsap.to(checks, {
            scale: 1,
            duration: 0.3,
            ease: 'power2.inOut'
          });
        }
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductComparison();
  });
} else {
  new ProductComparison();
}

// Reinitialize on Shopify section render (theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ProductComparison();
  });
}

/**
 * GSAP ScrollTrigger Integration (Optional Enhancement)
 * Uncomment if GSAP ScrollTrigger is available
 */
/*
if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);

  const comparisonSection = document.querySelector('.product-comparison');
  
  if (comparisonSection) {
    // Parallax effect on header
    gsap.to('.product-comparison__heading', {
      y: 50,
      opacity: 0.8,
      scrollTrigger: {
        trigger: comparisonSection,
        start: 'top top',
        end: 'bottom top',
        scrub: 1
      }
    });

    // Fade in table on scroll
    gsap.from('.product-comparison__table-container', {
      opacity: 0,
      y: 60,
      scrollTrigger: {
        trigger: '.product-comparison__table-container',
        start: 'top 80%',
        end: 'top 50%',
        scrub: 1
      }
    });
  }
}
*/
